import { faFileImage, faFilePdf, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ModalAtom, TextFieldAtom } from "../../../../components/atoms/atom";
import {
    clearUploadedFiles,
    processLetterhead,
    setLetterheadFile,
    setLogoFile,
    setUploadedLetterhead,
    setUploadedLogo
} from "../../../../store/slices/templateSlice";
import { showToast } from "../../../../utils/method";
import LetterheadPreview from "../components/LetterheadPreview";
import "../template.scss";

function AddTemplateModal(props) {
    const { show, onClose, onSubmit, mode, templateInfo = {}, loading } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Get loading state and uploaded files from Redux
    const {
        loading: templateLoading,
        uploadedFiles: {
            letterhead: uploadedLetterhead,
            logo: uploadedLogo,
        }
    } = useSelector((state) => state.templates);

    // Form fields
    const [name, setName] = useState(templateInfo?.name || "");
    const [isUploadingLetterhead, setIsUploadingLetterhead] = useState(false);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);

    // Letterhead configuration
    const [letterheadMargins, setLetterheadMargins] = useState({
        headerHeight: templateInfo?.letterheadMargins?.headerHeight || 200,
        footerHeight: templateInfo?.letterheadMargins?.footerHeight || 200
    });

    // Error states
    const [errorObj, setErrorObj] = useState({
        name: "",
        letterhead: "",
    });

    // Reset form when modal opens/closes or template info changes
    useEffect(() => {
        if (show) {
            setName(templateInfo?.name || "");

            // Handle letterhead - convert to expected format with proper data URL
            if (templateInfo?.letterhead) {
                if (typeof templateInfo.letterhead === 'string') {
                    // Edit mode: letterhead is a base64 string, ensure proper data URL prefix
                    const letterheadData = {
                        base64: templateInfo.letterhead,
                        preview: templateInfo.letterhead.startsWith('data:') ? templateInfo.letterhead : `data:image/png;base64,${templateInfo.letterhead}`,
                        ...(templateInfo.letterheadMetadata || {})
                    };
                    dispatch(setUploadedLetterhead(letterheadData));
                } else {
                    // New mode: letterhead is already an object
                    dispatch(setUploadedLetterhead(templateInfo.letterhead));
                }
            } else {
                dispatch(setUploadedLetterhead(null));
            }

            // Handle logo - convert to expected format with proper data URL
            if (templateInfo?.logo) {
                if (typeof templateInfo.logo === 'string') {
                    // Edit mode: logo is a base64 string, ensure proper data URL prefix
                    const logoData = {
                        base64: templateInfo.logo,
                        preview: templateInfo.logo.startsWith('data:') ? templateInfo.logo : `data:image/png;base64,${templateInfo.logo}`,
                        ...(templateInfo.logoMetadata || {})
                    };
                    dispatch(setUploadedLogo(logoData));
                } else {
                    // New mode: logo is already an object
                    dispatch(setUploadedLogo(templateInfo.logo));
                }
            } else {
                dispatch(setUploadedLogo(null));
            }

            setLetterheadMargins({
                headerHeight: templateInfo?.letterheadMargins?.headerHeight || 200,
                footerHeight: templateInfo?.letterheadMargins?.footerHeight || 200
            });
            dispatch(setLetterheadFile(null));
            dispatch(setLogoFile(null));
            setErrorObj({ name: "", letterhead: "" });
        } else {
            // Clear Redux state when modal closes
            dispatch(clearUploadedFiles());
        }
    }, [show, templateInfo, dispatch]);

    const validateForm = () => {
        let localErrorObj = {};

        const trimmedName = name ? name.trim() : "";

        if (!trimmedName) {
            localErrorObj.name = "templates.error-name-required";
        } else if (trimmedName.length > 255) {
            localErrorObj.name = "templates.error-name-too-long";
        }

        // Letterhead is mandatory
        if (!uploadedLetterhead) {
            localErrorObj.letterhead = "templates.error-letterhead-required";
        }

        setErrorObj({ ...localErrorObj });
        return Object.keys(localErrorObj).length === 0;
    };

    const onAddTemplate = () => {
        if (!validateForm()) {
            return;
        }

        onSubmit({
            name: name.trim(),
            letterheadMargins,
            uploadedLetterhead,
            uploadedLogo: uploadedLogo || null
        });
    };

    const handleLetterheadUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            showToast(t('templates.error-invalid-letterhead-type'), 'error');
            return;
        }

        // Validate file size (limit to 10MB)
        if (file.size > 10 * 1024 * 1024) {
            showToast(t('templates.error-letterhead-too-large'), 'error');
            return;
        }

        try {
            setIsUploadingLetterhead(true);

            // Use Redux action to process letterhead - it handles the response automatically
            await dispatch(processLetterhead(file)).unwrap();

            // Only set the file reference
            dispatch(setLetterheadFile(file));

            // Clear letterhead error if it exists
            setErrorObj(prev => ({ ...prev, letterhead: "" }));
        } catch (error) {
            console.error('Error uploading letterhead:', error);
            showToast(
                error?.response?.data?.message || error?.message || t('templates.error-uploading-letterhead'),
                'error'
            );
            dispatch(setUploadedLetterhead(null));
        } finally {
            setIsUploadingLetterhead(false);
        }
    };

    const handleLogoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            showToast(t('templates.error-invalid-logo-type'), 'error');
            return;
        }

        // Validate file size (limit to 5MB for logos)
        if (file.size > 5 * 1024 * 1024) {
            showToast(t('templates.error-logo-too-large'), 'error');
            return;
        }

        try {
            setIsUploadingLogo(true);

            // Create logo data with data URL prefix
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const logoData = {
                        base64: e.target.result,
                        preview: e.target.result, // Already includes data URL prefix
                        originalName: file.name,
                        originalMimeType: file.type,
                        originalSize: file.size,
                        processedSize: file.size,
                        width: img.width,
                        height: img.height
                    };
                    dispatch(setUploadedLogo(logoData));
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
            dispatch(setLogoFile(file));
        } catch (error) {
            console.error('Error uploading logo:', error);
            showToast(
                error?.response?.data?.message || error?.message || t('templates.error-uploading-logo'),
                'error'
            );
            dispatch(setUploadedLogo(null));
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const removeLetterhead = () => {
        dispatch(setUploadedLetterhead(null));
        dispatch(setLetterheadFile(null));
        const fileInput = document.getElementById('letterhead-upload');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const removeLogo = () => {
        dispatch(setUploadedLogo(null));
        dispatch(setLogoFile(null));
        const fileInput = document.getElementById('logo-upload');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    return (
        <ModalAtom
            title={t(mode === "add" ? "templates.add-template" : "templates.edit-template")}
            show={show}
            handleClose={onClose}
            handleSubmit={onAddTemplate}
            disabled={loading || templateLoading.create || templateLoading.update}
            size="xl"
            body={
                <Container fluid>
                    <Row>
                        {/* Left Column - Template Name and Logo */}
                        <Col md={5}>
                            <div className="pe-md-3">
                                <h6 className="mb-3">{t("templates.basic-info")}</h6>

                                {/* Template Name */}
                                <div className="mb-4">
                                    <TextFieldAtom
                                        type="text"
                                        placeholder={t("templates.template-name")}
                                        required
                                        value={name}
                                        maxLength={255}
                                        error={
                                            errorObj.name
                                                ? t(errorObj.name)
                                                : ""
                                        }
                                        onChange={(e) => {
                                            setName(e.target.value);
                                            setErrorObj({
                                                ...errorObj,
                                                name: e.target.value.trim()
                                                    ? ""
                                                    : "templates.error-name-required",
                                            });
                                        }}
                                    />
                                </div>
                                {/* Logo Upload Section */}
                                <h6 className="mb-3">{t("templates.logo")}</h6>
                                <p className="text-muted small mb-3">
                                    {t("templates.logo-description")}
                                </p>

                                {!uploadedLogo ? (
                                    <div className="border rounded p-3 mb-3 bg-light">
                                        <input
                                            type="file"
                                            id="logo-upload"
                                            accept=".jpg,.jpeg,.png,.gif,.webp"
                                            onChange={handleLogoUpload}
                                            className="form-control d-none"
                                            disabled={isUploadingLogo}
                                        />
                                        <label
                                            htmlFor="logo-upload"
                                            className={`btn btn-outline-primary btn-sm ${isUploadingLogo ? 'disabled' : ''}`}
                                        >
                                            {isUploadingLogo ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {t('common.uploading')}...
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                                                    {t('templates.select-logo-file')}
                                                </>
                                            )}
                                        </label>
                                        <small className="text-muted d-block mt-2">
                                            {t('templates.logo-file-requirements')}
                                        </small>
                                    </div>
                                ) : (
                                    <div className="border rounded p-3 mb-3 bg-light">
                                        {/* File Info */}
                                        <div className="d-flex align-items-center justify-content-between bg-white p-2 rounded border mb-3">
                                            <div className="d-flex align-items-center">
                                                <FontAwesomeIcon icon={faFileImage} className="text-success me-2" />
                                                <div>
                                                    <div className="fw-medium">{uploadedLogo.originalName || uploadedLogo.fileName || t('templates.logo')}</div>
                                                    <small className="text-muted">
                                                        {uploadedLogo.width && uploadedLogo.height ? (
                                                            <>
                                                                {uploadedLogo.width}×{uploadedLogo.height}px
                                                                {(uploadedLogo.processedSize || uploadedLogo.fileSize) && (
                                                                    <> • {((uploadedLogo.processedSize || uploadedLogo.fileSize) / 1024).toFixed(1)} KB</>
                                                                )}
                                                            </>
                                                        ) : (
                                                            t('templates.logo-file-generic')
                                                        )}
                                                    </small>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={removeLogo}
                                                title={t('common.remove')}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>

                                        {/* Logo Preview */}
                                        <div className="text-center border rounded bg-white p-3">
                                            <img
                                                src={uploadedLogo.preview || uploadedLogo.base64 || uploadedLogo}
                                                alt={t('templates.logo-preview-alt')}
                                                className="border rounded at-logo-preview"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>

                        {/* Right Column - Letterhead Upload and Configuration (More Space) */}
                        <Col md={7}>
                            <div className="ps-md-3">
                                <h6 className="mb-3">{t("templates.letterhead")} <span className="text-danger">*</span></h6>
                                <p className="text-muted small mb-3">
                                    {t("templates.letterhead-description")}
                                </p>

                                {!uploadedLetterhead ? (
                                    <div className="border rounded p-3 mb-3 bg-light">
                                        <input
                                            type="file"
                                            id="letterhead-upload"
                                            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                                            onChange={handleLetterheadUpload}
                                            className="form-control d-none"
                                            disabled={isUploadingLetterhead}
                                        />
                                        <label
                                            htmlFor="letterhead-upload"
                                            className={`btn btn-outline-primary btn-sm ${isUploadingLetterhead ? 'disabled' : ''}`}
                                        >
                                            {isUploadingLetterhead ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {t('common.uploading')}...
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                                                    {t('templates.select-letterhead-file')}
                                                </>
                                            )}
                                        </label>
                                        <small className="text-muted d-block mt-2">
                                            {t('templates.letterhead-file-requirements')}
                                        </small>
                                        {errorObj.letterhead && (
                                            <div className="text-danger small mt-2">
                                                {t(errorObj.letterhead)}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="border rounded p-3 mb-3 bg-light">
                                        {/* File Info */}
                                        <div className="d-flex align-items-center justify-content-between bg-white p-2 rounded border mb-3">
                                            <div className="d-flex align-items-center">
                                                <FontAwesomeIcon
                                                    icon={(uploadedLetterhead.originalMimeType || uploadedLetterhead.mimeType)?.includes('pdf') ? faFilePdf : faFileImage}
                                                    className={`me-2 ${(uploadedLetterhead.originalMimeType || uploadedLetterhead.mimeType)?.includes('pdf') ? 'text-danger' : 'text-success'}`}
                                                />
                                                <div>
                                                    <div className="fw-medium">{uploadedLetterhead.originalName || uploadedLetterhead.fileName || t('templates.letterhead')}</div>
                                                    <small className="text-muted">
                                                        {uploadedLetterhead.width && uploadedLetterhead.height ? (
                                                            <>
                                                                {uploadedLetterhead.width}×{uploadedLetterhead.height}px
                                                                {(uploadedLetterhead.processedSize || uploadedLetterhead.fileSize) && (
                                                                    <> • {((uploadedLetterhead.processedSize || uploadedLetterhead.fileSize) / 1024).toFixed(1)} KB</>
                                                                )}
                                                            </>
                                                        ) : (
                                                            t('templates.letterhead-file-generic')
                                                        )}
                                                    </small>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={removeLetterhead}
                                                title={t('common.remove')}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>

                                        {/* Enhanced 2-column layout for preview and margin controls */}
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <LetterheadPreview
                                                    uploadedLetterhead={uploadedLetterhead}
                                                    letterheadMargins={letterheadMargins}
                                                    onLetterheadMarginsChange={setLetterheadMargins}
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3 d-flex flex-column justify-content-center">
                                                <TextFieldAtom
                                                    label={t('templates.header-margin')}
                                                    type="number"
                                                    value={letterheadMargins.headerHeight}
                                                    onChange={(e) => setLetterheadMargins({
                                                        ...letterheadMargins,
                                                        headerHeight: Math.max(0, Math.min(3508, parseInt(e.target.value) || 0))
                                                    })}
                                                    suffix="px"
                                                    min="0"
                                                    max="3508"
                                                    className="text-end"
                                                />
                                                <TextFieldAtom
                                                    label={t('templates.footer-margin')}
                                                    type="number"
                                                    value={letterheadMargins.footerHeight}
                                                    onChange={(e) => setLetterheadMargins({
                                                        ...letterheadMargins,
                                                        footerHeight: Math.max(0, Math.min(3508, parseInt(e.target.value) || 0))
                                                    })}
                                                    suffix="px"
                                                    min="0"
                                                    max="3508"
                                                    className="text-end"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>


                    </Row>
                </Container>
            }
        />
    );
}

export default AddTemplateModal;